import type { Schemas } from '@ers/sdk';
import { useQuery } from '@tanstack/react-query';
import { api, call, isMissingRoute } from '../../api/client';
import { useSession } from '../../app/session';

/*
 * Доступ до управлінського контуру.
 * Управлінське коло (GET /api/v1/mgmt/me → inCircle) бачить управлінські подання: дашборд, координації, реєстр,
 * цілі й портфелі, огляди, мітки й коментарі кола. Учасники самих одиниць поза колом (виконавці, ініціатори,
 * учасники) працюють зі своїми одиницями: «Потребують дії», перелік і паспорт одиниці, звіти, приймання —
 * сервер показує їм лише доступні одиниці. Решта працівників модуля не бачить.
 */

export type MgmtMe = Schemas['MgmtMe'];

export const mgmtMeKey = ['mgmt', 'me'] as const;

/** Моє місце в управлінському колі (null — модуль не підключено або недоступний). */
export function useMgmtMe() {
  const { can } = useSession();
  return useQuery({
    queryKey: mgmtMeKey,
    enabled: can('mgmt:read'),
    staleTime: 10 * 60_000,
    retry: false,
    queryFn: async (): Promise<MgmtMe | null> => {
      try {
        return await call(api.GET('/api/v1/mgmt/me'));
      } catch (e) {
        if (isMissingRoute(e)) return null;
        throw e;
      }
    },
  });
}

/** Користувач у колі (false, поки відповіді немає). */
export function useInCircle(): boolean {
  return useMgmtMe().data?.inCircle === true;
}

/**
 * Модуль доступний: учасник кола, виконавець чи ініціатор одиниці або адресат запиту звіту (ознака сервера hasWork).
 * Для старого сервера без hasWork — пробний запит «мої одиниці».
 */
export function useMgmtAccess(): boolean {
  const { can } = useSession();
  const allowed = can('mgmt:read');
  const me = useMgmtMe();
  const inCircle = me.data?.inCircle === true;
  const hasWork = me.data && 'hasWork' in me.data ? me.data.hasWork : undefined;
  const probe = useQuery({
    queryKey: ['mgmt', 'unit-work-probe'],
    enabled: allowed && me.isSuccess && !!me.data && !inCircle && hasWork === undefined,
    staleTime: 10 * 60_000,
    retry: false,
    queryFn: async () => {
      try {
        const r = await call(api.GET('/api/v1/mgmt/units', { params: { query: { mine: 'any', limit: 1 } } }));
        return r.data.length > 0;
      } catch {
        return false;
      }
    },
  });
  return allowed && (inCircle || hasWork === true || probe.data === true);
}
